import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  PersistQueryClientOptions,
  PersistQueryClientProvider,
} from '@tanstack/react-query-persist-client';
import { Role, checkIfInvalidGrantError, rolesManipulator } from 'auth';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import { matchPath } from 'react-router-dom';
import { AppRoutes } from '@/AppRoutes.tsx';
import { getPersistentParamsSearch } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { COMPLIANCE_USER_SIGN_UP_NOW_QUERY_KEY } from '@/modules/auth/queries/user.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { publicPages } from '@/modules/common/routes.tsx';
import '@/modules/admin-dashboard/styles/common.css';
import { Spinner } from 'ui';

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const persistableQueries = {
  // [COMPLIANCE_CURRENT_USER_QUERY_KEY]: true,
  [COMPLIANCE_USER_SIGN_UP_NOW_QUERY_KEY]: true,
};

const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  dehydrateOptions: {
    dehydrateMutations: false,
    shouldDehydrateQuery: query =>
      (query.queryKey[0] as string) in persistableQueries && query.state.status === 'success',
  },
};

function App() {
  const auth = useAuth();
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [isCheckedAuth, setIsCheckedAuth] = useState(false);
  const isInvalidGrantError = checkIfInvalidGrantError(auth);
  const userRole = rolesManipulator.current_user_role;

  const handleInit = useCallback(async () => {
    const url = new URL(window.location.href);
    // todo: this is a temporary solution, remove this in future!
    if (url.searchParams.has('no-auth')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('no-auth');
      window.history.replaceState({}, '', url.toString());

      await auth.signoutSilent();

      // localStorage.clear();
      // sessionStorage.clear();
    }
    const isPublicAccessPage = publicPages.some(path => matchPath(path, location.pathname));

    if (!isPublicAccessPage && !isCheckedAuth && !auth.isLoading) {
      auth
        .signinSilent()
        .catch(error => {
          console.warn('[compliance] Silent sign-in failed:', error);
        })
        .finally(() => setIsCheckedAuth(true));
    } else {
      setIsCheckedAuth(true); // public page does not require authorization
    }
  }, [auth, isCheckedAuth]);

  useEffect(() => {
    // todo: this is a temporary solution, remove this in future!
    handleInit();
    // if (!isCheckedAuth && !auth.isLoading) {
    //   auth.signinSilent().then(setIsCheckedAuth.bind(null, true));
    // }
  }, [auth, handleInit, isCheckedAuth]);

  const handleHydrationSuccess = React.useCallback(() => {
    setIsHydrated(true);
    console.log('Query cache dehydrated ✅');
  }, []);

  useEffect(() => {
    const isPublicAccessPage = publicPages.some(path => matchPath(path, location.pathname));

    if (isInvalidGrantError && !auth.isLoading && !isPublicAccessPage) {
      auth.signinRedirect({
        redirect_uri: `${location.origin}${location.pathname}?${getPersistentParamsSearch(
          userRole || Role.COMPLIANCE_ADMIN,
        ).toString()}`,
      });
    }
  }, [auth, isInvalidGrantError, userRole]);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={handleHydrationSuccess}
    >
      {!isHydrated || !isCheckedAuth ? (
        <div className="flex h-[100dvh] w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <AppRoutes />
      )}
      <Toaster position="top-center" />
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
}

export default App;
